import { QrCodeIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { Trans, useLingui } from "@lingui/react/macro";
import { MouseEventHandler, useState } from "react";

import { useAppRouter } from "~/hook/base/useAppRouter.ts";
import { useDebouncedValue } from "~/hook/base/useDebouncedValue.ts";
import { useAppConfig } from "~/hook/domain/useAppConfig.ts";
import { usePoke } from "~/hook/domain/usePoke.ts";
import { User } from "~/service/dataType.ts";
import { dataUpdatedAtMiddleware } from "~/service/swr/middleware.dataUpdatedAt.ts";
import { createShouldAnimateMiddleware } from "~/service/swr/middleware.shouldAnimate.ts";
import { createUseSWRMiddleware } from "~/service/swr/middleware.ts";
import { isVerifiedUser } from "~/service/util.ts";
import { Navigation } from "~/ui/base/Navigation.tsx";
import { DomainBottomNavigation } from "~/ui/domain/DomainBottomNavigation.tsx";
import { UserListItem } from "~/ui/domain/UserListItem.tsx";
import { PokeWithAudioSheet } from "~/ui/overlay/PokeWithAudioSheet.tsx";
import { PokeWithDrawingSheet } from "~/ui/overlay/PokeWithDrawingSheet.tsx";
import { PokeWithEmojiSheet } from "~/ui/overlay/PokeWithEmojiSheet.tsx";
import { PokeWithGeoLocationSheet } from "~/ui/overlay/PokeWithGeolocationSheet.tsx";
import { PokeWithPictureSheet } from "~/ui/overlay/PokeWithPictureSheet.tsx";
import { QrScannerSheet } from "~/ui/overlay/QrScannerSheet.tsx";
import { useAuthNavigator } from "~/ui/provider/Auth.tsx";
import { Layer, useStackedLayer } from "~/ui/provider/StackedLayerProvider.tsx";

const cx = {
  animatedPokeOptionButton: "absolute right-0",

  hiddenAnimatedPokeOptionButton: "translate-x-1/4 scale-x-50 opacity-0",
  pokeOptionButton:
    "whitespace-pre rounded-full bg-black dark:bg-zinc-800 px-4 py-3 font-medium text-white duration-300 active:bg-zinc-300 dark:active:bg-zinc-700 disabled:bg-zinc-300 dark:disabled:bg-zinc-900",
};

const SEARCH_TEXT_KEY = "q";

type Key = [
  string,
  { email: string; limit: number; name: string; strategy?: string },
];

const isEqualKey = (a: Key, b: Key) =>
  a[1].email === b[1].email &&
  a[1].name === b[1].name &&
  a[1].strategy === b[1].strategy;

const useSWRMiddleware = createUseSWRMiddleware(
  createShouldAnimateMiddleware(isEqualKey),
  dataUpdatedAtMiddleware,
);

export const Search = () => {
  useAuthNavigator({ goToAuth: "/sign-in" });
  const overlay = useStackedLayer();
  const { t } = useLingui();
  const { params, replace } = useAppRouter();

  const searchText = params[SEARCH_TEXT_KEY] ?? "";
  const setSearchText = (searchText: string) => {
    replace({
      pathname: "/search",
      ...(searchText && { query: { [SEARCH_TEXT_KEY]: searchText } }),
    });
  };
  const deferredSearchText = useDebouncedValue(searchText, 300);
  const [selected, setSelected] = useState<null | User>(null);

  const {
    isAudioPokeEnabled,
    isDrawingPokeEnabled,
    isEmojiPokeEnabled,
    isGeolocationPokeEnabled,
    isNormalPokeEnabled,
    isPicturePokeEnabled,
    isPokeEnabled,
    searchUserStrategy,
  } = useAppConfig();

  const { data, dataUpdatedAt, isLoading, shouldAnimate } = useSWRMiddleware<
    User[],
    unknown
  >(
    [
      "user",
      {
        email: deferredSearchText,
        limit: 5,
        name: deferredSearchText,
        strategy: searchUserStrategy,
      },
    ],
    {
      keepPreviousData: true,
      onSuccess: () => {
        setSelected(null);
        setPokeOptionOpen(false);
      },
    },
  );

  const { isMutating, trigger } = usePoke();

  const [pokeOptionOpen, setPokeOptionOpen] = useState(false);

  const validateAndOverlay =
    (Sheet: Layer<{ email: string }>): MouseEventHandler<HTMLButtonElement> =>
    (e) => {
      if (e.target !== e.currentTarget) {
        return;
      }
      if (typeof selected?.email !== "string") {
        return;
      }
      overlay(Sheet, { email: selected.email });
    };

  const pokeOptions = [
    {
      enabled: isAudioPokeEnabled,
      key: "audio",
      label: <Trans>소리 찌르기 🎙️</Trans>,
      sheet: PokeWithAudioSheet,
    },
    {
      enabled: isPicturePokeEnabled,
      key: "picture",
      label: <Trans>사진 찌르기 📸</Trans>,
      sheet: PokeWithPictureSheet,
    },
    {
      enabled: isDrawingPokeEnabled,
      key: "drawing",
      label: <Trans>그림 찌르기 🎨</Trans>,
      sheet: PokeWithDrawingSheet,
    },
    {
      enabled: isGeolocationPokeEnabled,
      key: "geolocation",
      label: <Trans>내 위치 찌르기 📍</Trans>,
      sheet: PokeWithGeoLocationSheet,
    },
    {
      enabled: isEmojiPokeEnabled,
      key: "emoji",
      label: <Trans>이모티콘 찌르기 😊</Trans>,
      sheet: PokeWithEmojiSheet,
    },
  ].filter((option) => option.enabled);

  return (
    <div className="min-h-dvh">
      <Navigation />
      <div className="p-5">
        <div className="flex justify-between pt-[calc(8rem+var(--safe-top))]">
          <p className="text-2xl font-bold text-zinc-800 dark:text-zinc-100">
            <Trans>누구를 콕콕! 찌를까요?</Trans>
          </p>
          <button
            className="text-zinc-500 dark:text-zinc-400"
            onClick={() => {
              overlay(QrScannerSheet);
            }}
            type="button"
          >
            <QrCodeIcon className="size-6" />
          </button>
        </div>
        <div className="flex items-center pt-10">
          <span className="block w-5 text-xl font-bold">@</span>
          <input
            autoCapitalize="off"
            className="flex-1 rounded-none border-b-2 border-black bg-transparent py-2 text-xl font-bold outline-hidden placeholder:font-normal dark:border-white"
            data-testid="검색어"
            onChange={({ target: { value } }) => {
              setSearchText(value);
            }}
            placeholder={t`이름 또는 아이디로 검색해요`}
            type="text"
            value={searchText}
          />
        </div>
        <div
          className="mt-5 flex flex-col"
          data-testid="유저 컨테이너"
          style={{ height: 300 }}
        >
          {data?.map((user, i) => (
            <UserListItem
              animation={shouldAnimate ? { delayTimes: i } : null}
              isVerifiedUser={isVerifiedUser(user)}
              key={user.email + String(dataUpdatedAt)} // data 변경 시 애니메이션을 위해 dataUpdatedAt 추가
              onClick={() => {
                setSelected(user);
                setPokeOptionOpen(false);
              }}
              selected={selected?.email === user.email}
              userEmail={user.email}
              userName={user.name}
              userProfileImageUrl={user.profileImageUrl}
            />
          ))}
          {data?.length === 0 && (
            <div className="animate-from-bottom flex flex-col items-center pt-16 text-zinc-600 dark:text-zinc-400">
              <span className="text-zinc-400 dark:text-zinc-500">
                <XCircleIcon className="size-6" />
              </span>
              <p className="pt-6">
                <span className="font-semibold text-zinc-900 dark:text-zinc-100">
                  @{deferredSearchText}
                </span>{" "}
                <Trans>유저를 찾지 못했어요.</Trans>
              </p>
            </div>
          )}
        </div>
      </div>
      {isPokeEnabled && (
        <div
          className="fixed right-[max(1.25rem,calc(50vw-18.75rem))] bottom-[calc(128px+max(1.25rem,env(safe-area-inset-bottom)))]"
          data-testid="콕찌르기 버튼 컨테이너"
        >
          {pokeOptions.map((option, i) => {
            const offsetRem = String((pokeOptions.length - i) * 3.5);
            return (
              <button
                className={`${pokeOptionOpen ? "" : cx.hiddenAnimatedPokeOptionButton} ${cx.pokeOptionButton} ${cx.animatedPokeOptionButton}`}
                disabled={selected === null || isLoading || isMutating}
                key={option.key}
                onClick={validateAndOverlay(option.sheet)}
                style={{
                  bottom: `${offsetRem}rem`,
                  ...(pokeOptionOpen
                    ? {}
                    : { transform: `translateY(${offsetRem}rem)` }),
                }}
                type="button"
              >
                {option.label}
              </button>
            );
          })}
          <button
            className={`${pokeOptionOpen ? "w-36" : "w-28"} relative overflow-hidden ${cx.pokeOptionButton}`}
            data-testid="콕찌르기 버튼"
            disabled={
              selected === null ||
              isLoading ||
              isMutating ||
              (pokeOptionOpen && !isNormalPokeEnabled)
            }
            onClick={(e) => {
              if (e.target !== e.currentTarget) {
                return;
              }
              if (pokeOptionOpen && typeof selected?.email === "string") {
                if (!isNormalPokeEnabled) return;
                void trigger({
                  email: selected.email,
                  payload: { type: "normal" },
                }).then(() => {
                  setSelected(null);
                  setPokeOptionOpen(false);
                });
                return;
              }
              setPokeOptionOpen(true);
            }}
          >
            {pokeOptionOpen && isNormalPokeEnabled && (
              <Trans>바로 콕 찌르기</Trans>
            )}
            {(!pokeOptionOpen || !isNormalPokeEnabled) && (
              <Trans>콕 찌르기</Trans>
            )}
            {" 👉"}
          </button>
        </div>
      )}
      <DomainBottomNavigation />
    </div>
  );
};
